<template>
    <main class="main">
        <div class="container">
            <section-header
                title="Mój Biznes"
                sub-title="W tej zakładce znajdziesz najważniejsze informacje od Organizatora Sieci"
            />
            <div class="row">
                <div class="col-12">
                    <page-preloader
                        v-if="loader"
                        small
                        min-height="70vh"
                        :is-loading="true"
                    />
                    <div class="content" v-else>
                        <section
                            v-for="(section, sIdx) in sections"
                            :key="sIdx"
                        >
                            <div class="c-page-headline">
                                <sub-section-header :title="section.title"/>
                            </div>
                            <div class="row row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5">
                                <div
                                    v-for="(module, mIdx) in section.modules"
                                    class="col mb-4"
                                    :key="mIdx"
                                >
                                    <div
                                        class="card cursor-pointer square-col-content"
                                        @click="
                                            $router.push({
                                                ...module.route
                                            })
                                        "
                                    >
                                        <div class="card-body">
                                            <a-icon
                                                v-if="module.icon"
                                                :icon="module.icon"
                                            />
                                            <div
                                                v-else-if="module.image"
                                                :class="{ 'img-box': module.title }"
                                            >
                                                <h5 v-if="module.title" class="text-center">
                                                    {{ module.title }}
                                                </h5>
                                                <img
                                                    :src="module.image.src"
                                                    :width="module.image.width"
                                                    :height="module.image.height"
                                                    :style="
                                                        `max-width: ${module.image.width}`
                                                    "
                                                />
                                            </div>
                                            <h5 v-else class="text-center mb-0">
                                                {{ module.title }}
                                            </h5>
                                        </div>
                                        <div
                                            v-if="module.counter"
                                            v-text="module.counter"
                                            v-tooltip="module.counterTooltip"
                                            class="card-counter a-counter"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import networkMixin from './network/network.mixin'

export default {
    mixins: [networkMixin],
    data() {
        return {
            loader: false
        }
    },
    computed: {
        sections() {
            const newslettersTotal = this.$store.getters.getNewslettersTotal
            return [
                {
                    title: 'Newsy',
                    modules: [
                        {
                            title: 'System Kaucyjny',
                            route: {
                                name: 'newsletters'
                            },
                            counter: newslettersTotal && newslettersTotal > 99 ? '99+' : newslettersTotal,
                            counterTooltip: 'Liczba nieprzeczytanych newsletterów: ' + newslettersTotal,
                            image: {
                                src: require('@/assets/img/my-business/drs.svg'),
                                height: this.imgHeight
                            }
                        },
                        {
                            title: 'Tablica ogłoszeń',
                            route: {
                                name: 'announcements'
                            },
                            image: {
                                src: require('@/assets/img/my-business/announcements.svg'),
                                height: this.imgHeight
                            },
                            counter: this.$store.getters.getAnnouncementsTotal,
                            counterTooltip: this.$store.getters
                                .getAnnouncementsTotalTooltip
                        }
                    ]
                },
                ...(this.networksTerms.length ? [...this.networksTerms] : []),
                {
                    title: 'Marketing',
                    modules: [
                        {
                            title: 'Materiały gazetkowe',
                            route: {
                                name: 'newspaper-materials'
                            },
                            image: {
                                src: require('@/assets/img/my-business/newspaper-materials.svg'),
                                height: this.imgHeight
                            }
                        }
                    ]
                }
            ]
        },

        dictsSections() {
            return this.$store.getters.getMyBusinessSections
        },

        mbNetworks() {
            return this.$store.getters.getMyBusinessNetworks
        },

        networksTerms() {
            const terms = []

            // return [] for shopUser without RetrPermission
            if (this.$store.getters.isShopUser && !this.$store.getters.isRetrPermission) {
                return terms
            }

            const networks = this.networks

            if (!networks.length) {
                return terms
            } else if (networks.length === 1) {
                const network = networks[0]
                if (!network) {
                    return terms
                }

                const networkName = network.code.split('_')[0]

                terms.push({
                    title: `Warunki współpracy - ${networkName}`,
                    modules: this.subsections
                })
            } else {
                const modules = networks.map(network => {
                    // set route to 'my-business-retr' for shopUser with RetrPermission
                    if (this.$store.getters.isShopUser && this.$store.getters.isRetrPermission) {
                        return {
                            image: {
                                src: require(`@/assets/img/${network.code}.svg`),
                                width: 150,
                                height: 80
                            },
                            route: {
                                name: 'my-business-retr',
                                params: {
                                    alias: network.code.toLowerCase()
                                }
                            }
                        }
                    }
                    return {
                        image: {
                            src: require(`@/assets/img/${network.code}.svg`),
                            width: 150,
                            height: 80
                        },
                        route: {
                            name: 'my-business-terms',
                            params: {
                                alias: network.code.toLowerCase(),
                                newsId: this.sortedSectionsByNetwork(network)[0]?.id
                            }
                        },
                        counter: network.unreadCount,
                        counterTooltip: 'Liczba nieprzeczytanych materiałów: ' + network.unreadCount
                    }
                })

                terms.push({
                    title: 'Warunki współpracy',
                    modules
                })
            }

            return terms
        }
    },

    async created() {
        this.loader = true

        await this.$store.dispatch('fetchAnnouncementsTotal')
        await this.$store.dispatch('fetchNewslettersTotal')
        await this.$store.dispatch('extendedDataFetch', {
            includes: 'locations'
        })

        // Load all networks only to main user
        if (this.$store.getters.isMainUser) {
            await this.$store.dispatch('loadAllNetworks')
        }

        this.loader = false
    }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_custom.scss';
@import '~bootstrap/scss/mixins';
.square-col-content {
    height: 0;
    padding-top: calc(100% - 2px);
    position: relative;
}

.card {
    transition: 0.3s;
    &:hover {
        background-color: #f5f5f5;
        border: 1px solid var(--color-primary);
    }
    .card-body {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        padding: 2rem 1rem;
        @include media-breakpoint-down(sm) {
            padding: 1rem;
        }

        h5 {
            --max-lines: 4;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: var(--max-lines);
            line-clamp: var(--max-lines);
            -webkit-box-orient: vertical;
        }

        .img-box {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
            height: 100%;
            width: 100%;
            @include media-breakpoint-down(sm) {
                gap: 0.5rem;
            }

            h5 {
                --max-lines: 2;
                min-height: 2.5rem;
                @include media-breakpoint-down(sm) {
                    --max-lines: 2;
                    min-height: 2rem;
                    font-size: 13px;
                }
            }
        }

        &:hover {
            background-color: #f5f5f5;
        }
    }

    .a-counter {
        position: absolute;
        right: -6px;
        top: -6px;
        --size: 2rem;
        @include media-breakpoint-down(sm) {
            --size: 1.5rem;
            font-size: 12px;
        }
    }
}
</style>
